import { Duration, Service } from "../types/bookings"
import styles from '../styles/components/BookingFlowDuration.module.scss'
import { useState } from "react"
import Spinner from "./core/Spinner"
import { UserMembershipResponse } from "../types/memberships"
import { useToast } from "./core/ToastManager"
import { pushToDataLayer } from "../utils/tracking"

interface Props {
  services: Service[]
  durations: Duration[]
  groupAppointment?: boolean
  onSelectDuration: (duration: number, createMembership?: boolean) => void
  nextStep: () => void
  userMemberships: UserMembershipResponse | undefined
  isLoadingMemberships: boolean
  prevSelectedDuration?: number
  isPregnant?: boolean
  isMinor?: boolean
  isGuest?: boolean
  promotion?: string | null
  goToNextStep: () => void
}

export const BookingFlowDuration: React.FC<Props> = ({
  services,
  durations,
  groupAppointment,
  onSelectDuration,
  nextStep,
  userMemberships,
  isLoadingMemberships,
  prevSelectedDuration,
  isPregnant,
  isMinor,
  isGuest,
  promotion,
  goToNextStep
}) => {

  const { addToast } = useToast()
  const [activeTab, setActiveTab] = useState(durations[1]);

  const nonMemberActiveService = services.find((service) => service.duration === activeTab.serviceDurationMinutes);
  const disabled25 = nonMemberActiveService && nonMemberActiveService?.duration === 30 && (isPregnant || groupAppointment) ? true : false

  const getActiveMembershipDurations = () => {
    const activeDurations: number[] = [];
    if (userMemberships && userMemberships.membershipGroups) {
      userMemberships.membershipGroups.forEach((group) => {
        group.userMemberships.forEach((membership) => {
          if (membership.status === 'active') {
            activeDurations.push(membership.membership.durationInMinutes);
          }
        });
      });
    }
    return activeDurations;
  };

  const [selectedDuration, setSelectedDuration] = useState(prevSelectedDuration)

  const activeMembershipDurations = getActiveMembershipDurations();
  const hasMembership = activeMembershipDurations.length > 0;

  if (isLoadingMemberships) return <Spinner />;

  const handleDisabled25Check = (disabled25: boolean): boolean => {
    if (disabled25 && groupAppointment) {
      addToast('The 25 minute isn’t an option if you are booking a group appointment.', 'info');
      return true;
    }
    if (disabled25 && isPregnant) {
      addToast('The 25 minute isn’t an option if you are pregnant.', 'info');
      return true;
    }
    return false;
  };
  
  const handleContinueClick = () => {
    if (handleDisabled25Check(disabled25)) {
      return;
    }
    onSelectDuration(activeTab.displayDurationMinutes);
    pushToDataLayer({
      event: 'durationSelection',
      workflowName: 'booking',
      action: 'buttonClick',
    })
    nextStep();
  };
  
  const handleSignUpClick = () => {
    if (handleDisabled25Check(disabled25)) {
      return;
    }
    onSelectDuration(activeTab.displayDurationMinutes, true);
    pushToDataLayer({
      event: 'durationSelection',
      notes: 'booking with membership purchase option',
      workflowName: 'booking',
      action: 'buttonClick',
    })
    nextStep();
  };

  const handleMemberSelection = (duration: number, isDisabled?: boolean) => {
    if (isDisabled && handleDisabled25Check(isDisabled)) {
      return;
    }
    setSelectedDuration(duration)
    onSelectDuration(duration)
    goToNextStep()
  }

  const promotionBanner = () => {
    return (
      <div className={styles.promoBanner}>
        <p>{promotion}</p>
      </div>
    )
  }

  if (!hasMembership && !isGuest) return (
    <div>
      <div className={styles.tabs}>
        {durations.map((duration) => {
          if (duration.serviceDurationMinutes === 0) return null;
          return (
            <div
              className={`${styles.tab} ${activeTab === duration ? styles.active : ''}`}
              key={duration.serviceDurationMinutes}
              onClick={() => setActiveTab(duration)}
            >
              {duration.displayDurationMinutes} Min
            </div>
          );
        })}
      </div>
      <div className={`${styles.nonMemberOptions} ${disabled25 ? styles.disabled : ''}`}>
        {process.env.REACT_APP_SHOW_MEMBERS_PAY === '1' && !isMinor && activeTab.displayDurationMinutes !== 25 && (
        <div 
          className={`${styles.priceOption} ${selectedDuration === activeTab.displayDurationMinutes ? styles.selected : ''}`}
        >
          <p>Members Pay</p>
          <h2>${activeTab.memberPrice}</h2>
          <button className={`button inverted ${styles.memberCta} ${disabled25 ? styles.disabledBtn : '' }`} onClick={handleSignUpClick}>Become a Member</button>
        </div>
        )}
        <div className={styles.priceOption}>
          {process.env.REACT_APP_SHOW_MEMBERS_PAY === '1' ? <p>Non-Members Pay</p> : <p>{activeTab.displayDurationMinutes} min</p>}
          <h2>${activeTab.nonMemberPrice}</h2>
          <button className={`button ${styles.memberCta} ${disabled25 ? styles.disabledBtn : '' }`} onClick={handleContinueClick}>Select</button>
        </div>
      </div>
      {promotion && promotionBanner()}
    </div>
  );

  return (
    <div className={styles.membersDurations}>
      {durations.map((duration) => {
        if (duration.serviceDurationMinutes === 0) return null;
        const isDisabled = duration.serviceDurationMinutes === 30 && (isPregnant || groupAppointment)
        const price = hasMembership && !isGuest ? duration.memberPrice : duration.nonMemberPrice;
        return (
          <div
            className={`${styles.memberOption} ${selectedDuration === duration.displayDurationMinutes ? styles.selected : ""} ${isDisabled ? styles.disabled : ''}`}
            key={duration.serviceDurationMinutes}
            onClick={() => handleMemberSelection(duration.displayDurationMinutes, isDisabled)}
          >
            <p>{duration.displayDurationMinutes} Minutes</p>
            <h2>${price}</h2>
            {activeMembershipDurations.includes(duration.displayDurationMinutes) && !isGuest && (
              <div className={styles.yourMembership}>Your membership</div>
            )}
          </div>
        );
      })}
       {promotion && promotionBanner()}
    </div>
  );
};
